<template>
  <div>
    <!-- 投票态：百分比 + 双色按钮 -->
    <div v-if="!settled" :class="isPC ? 'w-[490px] mx-auto' : ''">
      <template v-if="showPercent">
        <!-- PC：百分比与进度条同行 -->
        <div v-if="isPC" class="mb-[20px] flex items-center gap-[8px]">
          <span class="text-[15px] font-medium leading-none shrink-0" :style="{ color: upColor }">{{ leftPercent }}%</span>
          <div class="h-[6px] flex-1 overflow-hidden rounded-[3px]" :style="{ background: progressGradient }"></div>
          <span class="text-[15px] font-medium leading-none shrink-0" :style="{ color: downColor }">{{ rightPercent }}%</span>
        </div>
        <!-- 移动端：百分比在上，进度条在下 -->
        <template v-else>
          <div class="mb-[8px] flex items-end justify-between px-[4px]">
            <span class="text-[24px] font-medium leading-none" :style="{ color: upColor }">{{ leftPercent }}%</span>
            <span class="text-[24px] font-medium leading-none" :style="{ color: downColor }">{{ rightPercent }}%</span>
          </div>
          <div class="mb-[20px] h-[6px] w-full overflow-hidden rounded-[3px]" :style="{ background: progressGradient }"></div>
        </template>
      </template>

      <div
        class="flex w-full overflow-hidden"
        :style="{ borderRadius: '39px', background: btnGradient }"
      >
        <button
          type="button"
          class="flex flex-1 items-center justify-center overflow-hidden border-none bg-transparent font-medium text-white outline-none"
          :class="isPC ? 'py-[16px] text-[24px] leading-[28px]' : 'pt-[22px] pb-[24px] text-[28px] leading-[32px]'"
          @click="leftOption && emit('select', leftOption)"
        >
          <img
            v-if="leftSelected"
            src="../images/icon-check.png"
            class="mr-[12px] h-[23px] w-[28px] shrink-0"
          />
          <img
            v-else-if="!hasSelection"
            src="../images/icon-up.png"
            class="mr-[12px] h-[24px] w-[36px] shrink-0"
          />
          <span class="truncate">{{
            leftOption ? $label(leftOption.label) : ''
          }}</span>
        </button>
        <button
          type="button"
          class="flex flex-1 items-center justify-center overflow-hidden border-none bg-transparent font-medium text-white outline-none"
          :class="isPC ? 'py-[16px] text-[24px] leading-[28px]' : 'pt-[22px] pb-[24px] text-[28px] leading-[32px]'"
          @click="rightOption && emit('select', rightOption)"
        >
          <span class="truncate">{{
            rightOption ? $label(rightOption.label) : ''
          }}</span>
          <img
            v-if="rightSelected"
            src="../images/icon-check.png"
            class="ml-[12px] h-[23px] w-[28px] shrink-0"
          />
          <img
            v-else-if="!hasSelection"
            src="../images/icon-down.png"
            class="ml-[12px] h-[36px] w-[36px] shrink-0"
          />
        </button>
      </div>
    </div>

    <!-- 结算态：结果对比卡片 -->
    <div v-else :class="isPC ? 'flex gap-[15px] h-[133px] justify-center' : 'flex gap-[8px]'">
      <!-- 左侧：实际走势 -->
      <div
        class="rounded-[16px]"
        :class="isPC ? 'pt-[16px] pl-[20px] pr-[22px] w-[233px] flex-none' : 'flex-1 px-[22px] pt-[26px] pb-[16px]'"
        :style="{ backgroundColor: winnerColor, color: isPush ? '#121212' : '#fff' }"
      >
        <div :class="isPC ? 'mb-[30px]' : 'mb-[20px]'">
          <span
            class="inline-block rounded-[4px]"
            :class="isPC ? 'text-[22px] leading-[25px]' : 'text-[24px] leading-[28px]'"
            >{{ $t('actualTrend') }}</span
          >
        </div>
        <div class="flex w-full text-[40px] leading-[46px]">
          {{ winnerLabel }}
        </div>
      </div>
      <!-- 右侧：我的分析 -->
      <div
        class="rounded-[16px] relative text-white border-solid"
        :class="isPC ? 'pt-[16px] pl-[20px] pr-[22px] w-[233px] flex-none' : 'flex-1 px-[22px] pt-[26px]'"
        :style="{
          backgroundColor: myVoteCardBg,
          borderWidth: isCorrect ? '1px' : '0',
          borderColor: isCorrect ? winnerColor : 'transparent',
        }"
      >
        <div :class="isPC ? 'mb-[30px]' : 'mb-[20px]'">
          <span
            class="inline-block rounded-[4px] text-[#7a7a7a]"
            :class="isPC ? 'text-[22px] leading-[25px]' : 'text-[24px] leading-[28px]'"
            >{{ pick($t, 'myAnalysis', 'myPrediction') }}</span
          >
        </div>
        <!-- 已参与且猜中 -->
        <div
          v-if="myVoteOption && isCorrect"
          class="flex text-[40px] leading-[46px]"
          :style="{ color: winnerColor }"
        >
          {{ $label(myVoteOption.label) }}
          <img
            :src="correctIcon"
            class="absolute top-[24px] right-[14px] h-[84px] w-[129px]"
          />
        </div>
        <!-- 未猜中 / 未参与 -->
        <div
          v-else
          class="flex text-[40px] leading-[46px]"
          :style="{ color: myVoteOption ? '#121212' : '#C3C3C3' }"
        >
          {{ myVoteOption ? $label(myVoteOption.label) : '--' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useLocaleString, useFegoPageInfo } from '@ark/web-shared/use';
import type { PredictOption } from '../../shared/types';
import { COLOR_GREEN, GAP_COLOR, MUTED_COLOR } from '../../shared/constants';
import { useWording } from '../hooks/useWording';
import locales from '../locales';
type ActualTrendLocaleKey = (typeof ACTUAL_TREND_LOCALE_KEY_BY_OPTION_INDEX)[keyof typeof ACTUAL_TREND_LOCALE_KEY_BY_OPTION_INDEX];
const __sfc_main = {};
__sfc_main.props = {
  options: {
    key: "options",
    required: false,
    type: Array,
    default: () => []
  },
  upColor: {
    key: "upColor",
    required: true,
    type: String
  },
  downColor: {
    key: "downColor",
    required: true,
    type: String
  },
  selectedCode: {
    key: "selectedCode",
    required: false,
    type: [String, null],
    default: null
  },
  settled: {
    key: "settled",
    required: false,
    type: Boolean,
    default: false
  },
  winnerCode: {
    key: "winnerCode",
    required: false,
    type: [String, null],
    default: null
  },
  showPercent: {
    key: "showPercent",
    required: false,
    type: Boolean,
    default: true
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const arrowGreenUp = require('../images/arrow-green-up.png');
  const arrowGreenDown = require('../images/arrow-green-down.png');
  const arrowRedUp = require('../images/arrow-red-up.png');
  const arrowRedDown = require('../images/arrow-red-down.png');
  const {
    useI18n
  } = fegoUse;
  const $t = useI18n(locales);
  const $label = useLocaleString();
  const {
    isPC
  } = useFegoPageInfo();
  const {
    pick
  } = useWording();
  const NEUTRAL_COLOR = '#999999';
  const ANGLE = '102deg';
  const UP_OPTION_INDEX = 0;
  const DOWN_OPTION_INDEX = 1;
  const ACTUAL_TREND_LOCALE_KEY_BY_OPTION_INDEX = {
    [UP_OPTION_INDEX]: 'actualTrendUp',
    [DOWN_OPTION_INDEX]: 'actualTrendDown'
  } as const;
  const props = __props;
  const emit = __ctx.emit;

  // ---- 投票态逻辑 ----
  const leftOption = computed<PredictOption | undefined>(() => props.options[0]);
  const rightOption = computed<PredictOption | undefined>(() => props.options[1]);
  const hasSelection = computed(() => props.selectedCode != null);
  const leftSelected = computed(() => hasSelection.value && leftOption.value && leftOption.value.code === props.selectedCode);
  const rightSelected = computed(() => hasSelection.value && rightOption.value && rightOption.value.code === props.selectedCode);
  const effectiveLeftColor = computed(() => rightSelected.value ? MUTED_COLOR : props.upColor);
  const effectiveRightColor = computed(() => leftSelected.value ? MUTED_COLOR : props.downColor);
  const rawLeftPercent = computed(() => leftOption.value && leftOption.value.percent != null ? leftOption.value.percent : 0);
  const rawRightPercent = computed(() => rightOption.value && rightOption.value.percent != null ? rightOption.value.percent : 0);
  const leftPercent = computed(() => rawLeftPercent.value);
  const rightPercent = computed(() => rawRightPercent.value);
  const progressGradient = computed(() => {
    const lp = rawLeftPercent.value === 0 && rawRightPercent.value === 0 ? 50 : leftPercent.value;
    return `linear-gradient(${ANGLE}, ${props.upColor} 0%, ${props.upColor} calc(${lp}% - 1px), ${GAP_COLOR} calc(${lp}% - 1px), ${GAP_COLOR} calc(${lp}% + 1px), ${props.downColor} calc(${lp}% + 1px), ${props.downColor} 100%)`;
  });
  const btnGradient = computed(() => {
    const lc = effectiveLeftColor.value;
    const rc = effectiveRightColor.value;
    return `linear-gradient(${ANGLE}, ${lc} 0%, ${lc} calc(50% - 2px), ${GAP_COLOR} calc(50% - 2px), ${GAP_COLOR} calc(50% + 2px), ${rc} calc(50% + 2px), ${rc} 100%)`;
  });

  // ---- 结算态逻辑 ----

  /** 根据选项在 options 中的位置确定颜色：[0]=涨色 [1]=跌色 其他=中性灰 */
  function colorForOption(option: PredictOption | undefined): string {
    if (!option) return NEUTRAL_COLOR;
    const idx = props.options.indexOf(option);
    if (idx === UP_OPTION_INDEX) return props.upColor;
    if (idx === DOWN_OPTION_INDEX) return props.downColor;
    return NEUTRAL_COLOR;
  }

  /**
   * 结算态展示的是标准涨跌方向，不复用选项配置里的 label。
   * 选项 label 可被运营自定义，只适合投票态和“我的分析”展示。
   */
  function getActualTrendLocaleKey(option: PredictOption | undefined): ActualTrendLocaleKey | null {
    if (!option) return null;
    const idx = props.options.indexOf(option);
    if (idx === UP_OPTION_INDEX) return ACTUAL_TREND_LOCALE_KEY_BY_OPTION_INDEX[UP_OPTION_INDEX];
    if (idx === DOWN_OPTION_INDEX) return ACTUAL_TREND_LOCALE_KEY_BY_OPTION_INDEX[DOWN_OPTION_INDEX];
    return null;
  }
  const winnerOption = computed(() => {
    if (!props.winnerCode) return undefined;
    return props.options.find(o => o.code === props.winnerCode);
  });
  const myVoteOption = computed(() => {
    if (!props.selectedCode) return undefined;
    return props.options.find(o => o.code === props.selectedCode);
  });
  const isPush = computed(() => props.settled && !winnerOption.value);
  const winnerLabel = computed(() => {
    if (isPush.value) return $t('actualTrendPush');
    const localeKey = getActualTrendLocaleKey(winnerOption.value);
    return localeKey ? $t(localeKey) : '';
  });
  const isCorrect = computed(() => props.winnerCode != null && props.selectedCode != null && props.winnerCode === props.selectedCode);
  const winnerColor = computed(() => {
    if (isPush.value) return isPC ? '#F6F7F9' : '#fff';
    return colorForOption(winnerOption.value);
  });
  const myVoteCardBg = computed(() => {
    if (!isCorrect.value) return isPC ? '#F6F7F9' : '#fff';
    return winnerColor.value === props.upColor ? '#F7FAFF' : '#FFF9F5';
  });
  const ICON_MAP: Record<string, string> = {
    'green-up': arrowGreenUp,
    'green-down': arrowGreenDown,
    'red-up': arrowRedUp,
    'red-down': arrowRedDown
  };
  const correctIcon = computed(() => {
    if (!props.winnerCode) return '';
    const idx = props.options.findIndex(o => o.code === props.winnerCode);
    if (idx < 0) return '';
    const isUp = idx === UP_OPTION_INDEX;
    const color = isUp ? props.upColor : props.downColor;
    const colorName = color === COLOR_GREEN ? 'green' : 'red';
    const direction = isUp ? 'up' : 'down';
    return ICON_MAP[`${colorName}-${direction}`] || '';
  });
  return {
    $t,
    $label,
    isPC,
    pick,
    emit,
    leftOption,
    rightOption,
    hasSelection,
    leftSelected,
    rightSelected,
    leftPercent,
    rightPercent,
    progressGradient,
    btnGradient,
    myVoteOption,
    isPush,
    winnerLabel,
    isCorrect,
    winnerColor,
    myVoteCardBg,
    correctIcon
  };
};
export default __sfc_main;
</script>
